import { useState, FocusEvent, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Autocomplete, { type AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import { useController, type FieldValues } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import { IOption } from '@cbridge-utils/options';
import { FieldControlInputContainer } from '../_components/FieldControlInputContainer/FieldControlInputContainer';
import { FieldControlLabelContainer } from '../_components/FieldControlLabelContainer/FieldControlLabelContainer';
import { getValueAsOption } from './_helpers/getValueAsOption';
import { isOptionEqualToValue } from './_helpers/isOptionEqualToValue';
import { renderHighlightedLabel } from './_helpers/renderHighlightedLabel';
import { IAutocompleteFieldControlProps, IAutocompleteValueType, RenderOption } from './AutocompleteFieldControl.interface';

const OptionContent = styled('div')`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1 1 0;
`;

const defaultRenderOption: RenderOption = ({ key, ...props }, option) => (
  <Box
    key={key}
    component={'li'}
    {...props}
  >
    <OptionContent>
      {option.label}
    </OptionContent>
  </Box>
);

const highlightRenderOption: RenderOption = ({ key, ...props }, option, state) => (
  <Box
    key={key}
    component={'li'}
    {...props}
  >
    <OptionContent>
      {renderHighlightedLabel(option.label, state.inputValue)}
    </OptionContent>
  </Box>
);

const groupByOption = (option: IOption) => option.group ?? '';

export const AutocompleteFieldControl = <T extends FieldValues>(props: IAutocompleteFieldControlProps<T>) => {
  const {
    name,
    label,
    tooltip,
    action,
    required,
    readOnly,
    isLoading,
    options,
    placeholder,
    freeSolo = true,
    grouped,
    highlightMatch,
    disableClearable,
    getOptionDisabled,
    renderOption,
    renderInput: customRenderInput,
    mixedValues,
    bulkEdit,
  } = props;
  const { t } = useTranslation();

  const [inputValue, setInputValue] = useState('');

  const {
    field: {
      onChange,
      disabled,
      value = null,
      ...restFieldProps
    },
    fieldState: { invalid, error },
  } = useController({
    name,
  });

  const handleChange = (_: SyntheticEvent, newValue: IAutocompleteValueType) => {
    if (newValue === null) {
      onChange(null);
    } else if (typeof newValue === 'string') {
      const trimmedValue = newValue.trim();
      onChange(trimmedValue);
    } else {
      onChange(newValue.value);
    }
  };

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (freeSolo) {
      handleChange(event, inputValue);
    }
  };

  const handleInputChange = (_: SyntheticEvent, newInputValue: string) => {
    setInputValue(newInputValue);
  };

  const isInputDisabled = disabled || readOnly;

  // In read-only mode the action (e.g. a link button) is rendered inside the
  // input as an adornment, so the field can take up the full width. In edit
  // mode it stays outside, because inputs may have built-in adornments.
  const showActionInside = isInputDisabled && Boolean(action);

  const isValueEmpty = value === undefined || value === null || value === '';
  const showMixedPlaceholder = Boolean(mixedValues) && isValueEmpty;
  const resolvedPlaceholder = showMixedPlaceholder ? t('text:common.bulkEdit.mixedValues') : placeholder;

  const defaultRenderInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      error={invalid}
      required={required}
      placeholder={resolvedPlaceholder}
      sx={showMixedPlaceholder ? {
        '& input::placeholder': {
          fontStyle: 'italic',
          color: '#00897B',
          opacity: 1,
        },
      } : undefined}
      slotProps={{
        ...params.slotProps,
        input: showActionInside ? {
          ...params.slotProps.input,
          endAdornment: (
            <>
              {params.slotProps.input?.endAdornment}
              <InputAdornment position={'end'}>{action}</InputAdornment>
            </>
          ),
        } : params.slotProps.input,
      }}
    />
  );

  // MUI requires options pre-sorted by group, otherwise it renders duplicate group headers.
  const resolvedOptions = grouped
    ? [...options].sort((a, b) => groupByOption(a).localeCompare(groupByOption(b)))
    : options;

  const resolvedRenderOption = renderOption
    ?? (highlightMatch ? highlightRenderOption : defaultRenderOption);

  // In freeSolo mode a committed custom value is kept as a raw string: mapping it
  // to null makes MUI v9 clear the visible input while the form still holds the value.
  const valueSafe = getValueAsOption(value, resolvedOptions) ?? (disabled || freeSolo ? value : null);

  return (
    <FieldControlLabelContainer
      label={label}
      tooltip={tooltip}
      htmlFor={name}
      disabled={isInputDisabled}
      error={invalid}
      required={required}
    >
      <FieldControlInputContainer
        name={name}
        errorMessage={error?.message}
        action={showActionInside ? undefined : action}
        bulkEdit={bulkEdit}
      >
        <Autocomplete
          {...restFieldProps}
          id={name}
          loading={isLoading}
          freeSolo={freeSolo}
          multiple={false}
          disableClearable={disableClearable}
          disabled={isInputDisabled}
          options={resolvedOptions}
          groupBy={grouped ? groupByOption : undefined}
          onChange={handleChange}
          onBlur={freeSolo ? handleBlur : undefined}
          inputValue={inputValue}
          onInputChange={handleInputChange}
          fullWidth={true}
          getOptionDisabled={getOptionDisabled}
          renderOption={resolvedRenderOption}
          renderInput={customRenderInput || defaultRenderInput}
          isOptionEqualToValue={isOptionEqualToValue}
          value={valueSafe}
          // In read-only mode there is no popup icon, so disabling it drops the
          // `hasPopupIcon` class and the paddingRight MUI reserves for it.
          forcePopupIcon={showActionInside ? false : undefined}
          sx={showActionInside ? {
            // Take the action adornment out of absolute positioning so it sits
            // flush at the end of the full-width input instead of overlapping it.
            '& .MuiAutocomplete-endAdornment': { position: 'static', transform: 'none' },
          } : undefined}
        />
      </FieldControlInputContainer>
    </FieldControlLabelContainer>
  );
};
